import { useCallback, useEffect, useState } from 'react';
import type { Framework, NeedsAttentionCategory, Site } from '@mvcp/contracts';
import type { Density, Theme } from '../theme';
import { getSites, listApplicableFrameworks, listCompanies } from '../api';
import { ComplianceDashboard } from './ComplianceDashboard';
import { ComplianceDirectory } from './ComplianceDirectory';
import { DeviceDirectory } from './DeviceDirectory';
import { FrameworkDetailView } from './FrameworkDetailView';
import { ImportWizard } from './ImportWizard';
import { NeedsAttentionView } from './NeedsAttentionView';
import { NotificationBell } from './NotificationBell';
import { PeopleView } from './PeopleView';
import { ReportGeneratorModal, type ScopeListStatus } from './ReportGeneratorModal';
import { SeedCatalogView } from './SeedCatalogView';
import { ShellLayout, type NavItem } from './ShellLayout';
import { SitesView } from './SitesView';
import { VendorDirectory } from './VendorDirectory';

/**
 * A cockpit drill target (Story 4.4): the record kind + its id. Defined here (the
 * cockpit's drill controller owns it) and reused across the fleet views and the
 * Dashboard as the single drill-target vocabulary. Opening a record also selects the
 * record's section — the section is resolved by matching `KIND_LABEL[kind]` against
 * the `nav` array's labels, so it can never desync from a nav reorder/insert.
 */
export type DrillTarget = { kind: 'site' | 'vendor' | 'device'; id: string };

/** `DrillTarget.kind` → the label of its nav slot (looked up in `nav` at runtime). */
const KIND_LABEL: Record<DrillTarget['kind'], string> = {
  site: 'Sites',
  vendor: 'Vendors',
  device: 'Devices',
};

/** Compliance Manager cockpit (FR-1/UX-DR9). Full surfaces land in Epics 2-4. */
export function CockpitShell({
  theme,
  density,
  onToggleTheme,
  onToggleDensity,
}: {
  theme: Theme;
  density: Density;
  onToggleTheme: () => void;
  onToggleDensity: () => void;
}): React.JSX.Element {
  // Resolve the Manager's own Company maritime flag (Story 3.2, FR-15). A Manager's
  // `listCompanies()` is RLS-scoped to their single Company. Fail CLOSED: the
  // maritime-only Site fields (ship option + IMO/flag/vessel) stay HIDDEN until the
  // flag is affirmatively resolved to `true` (AC2 — hidden, not merely empty). Every
  // unresolved state — initial load, empty or failed `listCompanies()` — is treated
  // as non-maritime, so a non-maritime Manager never sees ship fields even for a
  // frame, and a fetch error safely shows office-only until reload.
  const [companyMaritime, setCompanyMaritime] = useState(false);
  // The Framework whose detail view is open in the content pane (Story 4.3). When set,
  // it supersedes the active nav item's content (in-pane full-page + Back — no router).
  const [frameworkDetailId, setFrameworkDetailId] = useState<string | null>(null);
  // The Needs-Attention surface's active category when open (Story 4.2), or null when
  // closed. Mirrors `frameworkDetailId`: a `contentOverride` in-pane full-page + Back
  // (no router). `'all'` = the section "View all →"; a category = a tile click.
  const [needsAttentionCategory, setNeedsAttentionCategory] = useState<
    NeedsAttentionCategory | 'all' | null
  >(null);

  // ---- Sidebar count badges -------------------------------------------------
  // DESIGN.md's nav item carries "count badges inline". The counts are LIFTED from the
  // dashboard rather than fetched here: `ComplianceDashboard` already calls `GET /dashboard`
  // on mount, whose `DashboardSummary` states `sitesCount` and `vendorsCount` outright, so
  // reporting them upward costs zero additional round-trips — which is the whole constraint
  // (a badge is decoration; it must not add load to a compliance workspace).
  //
  // Two consequences worth knowing before changing this:
  //   * The badges appear a beat AFTER first paint, when that fetch resolves. `null` until
  //     then, so the nav renders with no badge rather than with a wrong `0`.
  //   * They persist once known. The dashboard unmounts when you navigate away (only the
  //     active nav item's content is rendered), but this state outlives it, so the badges
  //     stay put instead of flickering off on every section change.
  const [navCounts, setNavCounts] = useState<{ sites: number; vendors: number } | null>(null);

  // ---- Report generator (Story 6.1) ----------------------------------------
  // A single `reportScope` opens the shared scope-picker from every entry point; only
  // the pre-set framework differs (dashboard = all applicable → null; a Framework
  // detail = that framework's id). `null` = closed. The applicable framework + site
  // lists (and their load status) are loaded lazily on open and passed to the modal,
  // which handles the loading/loaded/failed states + re-seeds its default when they
  // arrive after open (never a stuck-empty selection / false "no frameworks apply").
  const [reportScope, setReportScope] = useState<{ frameworkId: string | null } | null>(null);
  const [reportFrameworks, setReportFrameworks] = useState<Framework[]>([]);
  const [reportSites, setReportSites] = useState<Site[]>([]);
  const [scopeStatus, setScopeStatus] = useState<ScopeListStatus>('loading');

  const loadScopeLists = useCallback(async () => {
    setScopeStatus('loading');
    try {
      const [frameworks, sites] = await Promise.all([listApplicableFrameworks(), getSites()]);
      setReportFrameworks(frameworks);
      setReportSites(sites);
      setScopeStatus('loaded');
    } catch {
      setScopeStatus('failed');
    }
  }, []);

  const openReport = useCallback(
    (frameworkId: string | null) => {
      setReportScope({ frameworkId });
      // Drop any cached lists from a prior open and refetch, so the modal never seeds
      // its default selection from a stale applicable-framework/site set.
      setReportFrameworks([]);
      setReportSites([]);
      setScopeStatus('loading');
      void loadScopeLists();
    },
    [loadScopeLists],
  );

  // ---- Drill controller (Story 4.4) ----------------------------------------
  // A back stack over the existing index/state nav — NO router. Each `openRecord`
  // pushes a record; `back` pops one. The controlled `activeIndex` is derived from
  // the stack top (its section) or, when the stack is empty, from `preDrillIndex`
  // — the sidebar section the drill started from, so Back always restores the exact
  // prior record AND section without losing place.
  const [drillStack, setDrillStack] = useState<DrillTarget[]>([]);
  // The sidebar section a drill originated from (the "pre-drill" index). Only a
  // sidebar select moves it; drilling never does — so popping the stack empty
  // lands back exactly where the drill began (e.g. the Dashboard).
  const [preDrillIndex, setPreDrillIndex] = useState(0);

  const top = drillStack[drillStack.length - 1] ?? null;

  const openRecord = useCallback((t: DrillTarget) => {
    setDrillStack((s) => [...s, t]);
  }, []);
  const back = useCallback(() => {
    setDrillStack((s) => s.slice(0, -1));
  }, []);
  // Selecting any sidebar item resets the drill stack + Framework-detail override and
  // records the new origin section, so the content pane never lags the topbar title /
  // aria-current (Story 4.3/4.4).
  const onNavSelect = useCallback((i: number) => {
    setPreDrillIndex(i);
    setDrillStack([]);
    setFrameworkDetailId(null);
    setNeedsAttentionCategory(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const companies = await listCompanies();
        if (!cancelled) setCompanyMaritime(companies[0]?.maritime === true);
      } catch {
        // Fail closed: leave `companyMaritime` false so ship fields stay hidden.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // `focusId` for a section = the drilled record's id when the stack top is in that
  // section, else null (the section shows its standalone list). Each fleet view keys
  // its DETAIL subcomponent by that id, so a repeated/cross drill to a new record in
  // the same section re-opens cleanly (fresh fetch) while the list's loaded data
  // survives the transition.
  const siteFocus = top && top.kind === 'site' ? top.id : null;
  const vendorFocus = top && top.kind === 'vendor' ? top.id : null;
  const deviceFocus = top && top.kind === 'device' ? top.id : null;

  const nav: NavItem[] = [
    {
      label: 'Dashboard',
      icon: 'layout',
      content: (
        <ComplianceDashboard
          companyMaritime={companyMaritime}
          onOpenFramework={setFrameworkDetailId}
          onOpenRecord={openRecord}
          onViewAllNeedsAttention={(c) => setNeedsAttentionCategory(c ?? 'all')}
          onExportReport={() => openReport(null)}
          onCounts={setNavCounts}
        />
      ),
    },
    {
      label: 'Sites',
      icon: 'ship',
      ...(navCounts ? { count: navCounts.sites } : {}),
      content: (
        <SitesView
          companyMaritime={companyMaritime}
          focusId={siteFocus}
          onOpenRecord={openRecord}
          onBack={back}
        />
      ),
    },
    {
      label: 'Vendors',
      icon: 'package',
      ...(navCounts ? { count: navCounts.vendors } : {}),
      content: <VendorDirectory focusId={vendorFocus} onOpenRecord={openRecord} onBack={back} />,
    },
    {
      label: 'Devices',
      icon: 'cpu',
      // NO count, deliberately. The prototype badges Devices too, but `DashboardSummary`
      // carries only `sitesCount` / `vendorsCount`, and the device total lives behind
      // `DeviceDirectory`'s own paginated fetch — which would mean a request issued purely to
      // fill a badge, or a badge showing the current PAGE size instead of the total. An
      // absent badge is the honest option; see `NavItem.count`.
      content: <DeviceDirectory focusId={deviceFocus} onOpenRecord={openRecord} onBack={back} />,
    },
    { label: 'Import', icon: 'upload', content: <ImportWizard /> },
    {
      label: 'Compliance',
      icon: 'shield-check',
      content: <ComplianceDirectory onOpenFramework={setFrameworkDetailId} />,
    },
    { label: 'Catalog', icon: 'book', content: <SeedCatalogView /> },
    {
      label: 'Reports',
      icon: 'file-text',
      content: (
        <section className="card stack" aria-labelledby="reports-heading">
          <h1 id="reports-heading" style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
            Reports
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            Export a defensible PDF compliance dossier for an auditor or class surveyor, scoped by
            framework(s), site/vessel(s), and an as-of date.
          </p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => openReport(null)}
            style={{ alignSelf: 'flex-start' }}
          >
            Generate report
          </button>
        </section>
      ),
    },
    { label: 'People', icon: 'users', content: <PeopleView /> },
  ];

  // Controlled active index: the drilled record's section (resolved by matching the
  // kind's label against `nav`, so it survives a nav reorder), else the pre-drill
  // origin section. A missing label (defensive) falls back to the origin.
  const topIndex = top ? nav.findIndex((n) => n.label === KIND_LABEL[top.kind]) : -1;
  const activeIndex = topIndex >= 0 ? topIndex : preDrillIndex;

  return (
    <>
    <ShellLayout
      roleLabel="Compliance Manager"
      navItems={nav}
      theme={theme}
      density={density}
      onToggleTheme={onToggleTheme}
      onToggleDensity={onToggleDensity}
      activeIndex={activeIndex}
      notificationBell={<NotificationBell onOpenRecord={openRecord} />}
      contentOverride={
        frameworkDetailId ? (
          <FrameworkDetailView
            frameworkId={frameworkDetailId}
            onBack={() => setFrameworkDetailId(null)}
            onCreateReport={(id) => openReport(id)}
          />
        ) : needsAttentionCategory !== null ? (
          <NeedsAttentionView
            initialCategory={needsAttentionCategory}
            onBack={() => setNeedsAttentionCategory(null)}
            onOpenRecord={(t) => {
              setNeedsAttentionCategory(null);
              openRecord(t);
            }}
          />
        ) : undefined
      }
      onNavSelect={onNavSelect}
    />
    {reportScope !== null && (
      <ReportGeneratorModal
        frameworks={reportFrameworks}
        sites={reportSites}
        listStatus={scopeStatus}
        preselectedFrameworkId={reportScope.frameworkId}
        onRetryLoad={() => void loadScopeLists()}
        onClose={() => setReportScope(null)}
      />
    )}
    </>
  );
}
